import React from 'react';
import { motion } from 'framer-motion';
import { Spotlight } from './ui/spotlight';
import { useTheme } from '../contexts/ThemeContext';
import {
  BillingMode,
  ProductCatalogItem,
  ProductTierId,
  calculateAddOnPricing,
  fallbackCatalogItems,
  formatRand,
  PricedCatalogItem,
  productPlans,
} from '../lib/productPlans';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const modules = [
  'CRM',
  'Invoicing',
  'Online shop',
  'Google login',
  'Facebook login',
  'Guided requests',
  'Client portal',
  'Analytics',
];

const processSteps = [
  {
    title: 'Choose the build',
    copy: 'Pick the tier that matches the business now. Bolt-ons stay modular, so the system can grow without restarting.',
  },
  {
    title: 'Create the client account',
    copy: 'The buyer signs in once, then every brief, payment, checklist, milestone, and request lives in the portal.',
  },
  {
    title: 'Guided specification',
    copy: 'A guided intake turns messy ideas into a clear brief, then presents the final checklist for approval.',
  },
  {
    title: 'Build with visibility',
    copy: 'The client can follow progress, send requests, and watch the site move from discovery to launch.',
  },
];

type DraftMessage = {
  role: 'assistant' | 'client';
  content: string;
};

type PackageDecision = {
  summary: string;
  confirmedIds: string[];
  recommendedIds: string[];
  questionIds: string[];
  questions: string[];
  confidence: number;
};

type ProductPreviewResult = {
  reply?: string;
  package?: {
    summary?: string;
    confirmedItemIds?: string[];
    recommendedItemIds?: string[];
    questionItemIds?: string[];
    questions?: string[];
    confidence?: number;
  };
};

type DomainAvailabilityStatus =
  | 'unchecked'
  | 'manual'
  | 'available'
  | 'unavailable'
  | 'premium'
  | 'invalid'
  | 'check_failed'
  | 'error';

type DomainCheckResult = {
  ok?: boolean;
  domain?: string;
  mode?: string;
  availabilityStatus?: DomainAvailabilityStatus;
  isAvailable?: boolean | null;
  isPremium?: boolean | null;
  purchaseAmount?: number;
  message?: string;
  reason?: string;
  checkFailureCode?: string;
};

type DomainPreference = {
  needsRegistration: boolean;
  desiredDomain: string;
  availabilityStatus?: DomainAvailabilityStatus;
  availabilityMode?: string;
  availabilityMessage?: string;
  isAvailable?: boolean | null;
  isPremium?: boolean | null;
  premiumReason?: string;
  purchaseAmount?: number;
};

type PricingSnapshot = {
  basePrice: number;
  addOnOriginalTotal: number;
  addOnDiscountTotal: number;
  addOnTotal: number;
  domainRegistrationTotal: number;
  total: number;
  items: Array<{
    id: string;
    label: string;
    originalPrice: number;
    adjustedPrice: number;
    discountAmount: number;
    discountLabel?: string;
  }>;
};

const firstQuestionByTier: Record<ProductTierId, string> = {
  launch: 'Hey, what type of website do you want? And do you own a domain, or should we grab one for you?',
  growth: 'Hey, what type of website do you want? And do you own a domain, or should we grab one for you?',
  command: 'Hey, what type of website do you want? And do you own a domain, or should we grab one for you?',
};

const INTAKE_TOTAL_QUESTIONS = 6;

const buildScriptedReply = (clientMessageCountAfterSend: number): string => {
  if (clientMessageCountAfterSend >= INTAKE_TOTAL_QUESTIONS) {
    return 'Thank you. Your site spec is ready below.';
  }
  const offline = [
    '',
    'Cool. Any features you want? For example a contact form, online shop, login area, payments, or AI.',
    'Got it. What visual vibe should we aim for? Minimal, bold, premium, playful, or something specific.',
    'Noted. Anything about pages, content updates, blog, or social — mostly static or something more active?',
    'Timing or constraints I should know — launch date, phased phases, or integrations already promised?',
    'Almost there — anything we have not covered? CRM, invoicing, bookings, memberships, something unusual?',
  ];
  return offline[clientMessageCountAfterSend] || 'Tell me anything else that matters for the build.';
};

const wantsDomainRegistration = (message: string, previousMessages: DraftMessage[]) => {
  const lowerMessage = message.toLowerCase();
  const previousAssistantAskedDomain = [...previousMessages]
    .reverse()
    .some((item) => item.role === 'assistant' && /\bdomain\b/i.test(item.content));

  return (
    /\b(no domain|do not have a domain|don't have a domain|dont have a domain|do not own a domain|don't own a domain|dont own a domain|need a domain|need domain)\b/i.test(message) ||
    /\b(buy|register|purchase|handle|get)\b.{0,40}\bdomain\b/i.test(message) ||
    (previousAssistantAskedDomain && /\b(no|nope|not yet|don't have one|dont have one|don't own one|dont own one|please buy|you buy|you register|handle it)\b/i.test(lowerMessage))
  );
};

const hasExistingDomain = (message: string, previousMessages: DraftMessage[]) => {
  const previousAssistantAskedDomain = [...previousMessages]
    .reverse()
    .some((item) => item.role === 'assistant' && /\bdomain\b/i.test(item.content));

  return (
    /\b(already have|have a domain|own a domain|got a domain|domain is)\b/i.test(message) ||
    (previousAssistantAskedDomain && /\b(yes|yep|yeah|i have one|we have one|already)\b/i.test(message.toLowerCase()))
  );
};

const hasExplicitAiIntent = (messages: DraftMessage[]) =>
  messages
    .filter((message) => message.role === 'client')
    .some((message) => /\b(ai|artificial intelligence|assistant|bot|automation|automate|intelligent|mistral|llm|agent|suite)\b/i.test(message.content));

const removeUnrequestedAiSuite = (decision: PackageDecision, messages: DraftMessage[]): PackageDecision => {
  if (hasExplicitAiIntent(messages)) return decision;

  return {
    ...decision,
    confirmedIds: decision.confirmedIds.filter((id) => id !== 'ai_suite'),
    recommendedIds: decision.recommendedIds.filter((id) => id !== 'ai_suite'),
    questionIds: decision.questionIds.filter((id) => id !== 'ai_suite'),
  };
};

const checkDesiredDomain = async (domain: string): Promise<DomainCheckResult> => {
  try {
    const response = await fetch('/api/domain-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain }),
    });
    const data = (await response.json().catch(() => null)) as DomainCheckResult | null;

    if (data && typeof data === 'object') {
      return data;
    }

    return {
      ok: false,
      domain,
      availabilityStatus: 'check_failed',
      checkFailureCode: 'invalid_response',
      message: "We couldn't read the registrar's reply. Try again or pick this domain anyway.",
    };
  } catch {
    return {
      ok: false,
      domain,
      availabilityStatus: 'check_failed',
      checkFailureCode: 'network_error',
      message: "We couldn't reach the registrar right now. Try again or pick this domain anyway.",
    };
  }
};

const buildDomainAlternatives = (rawInput: string): string[] => {
  const cleaned = rawInput.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
  if (!cleaned) return [];
  const dotIndex = cleaned.indexOf('.');
  const sld = dotIndex === -1 ? cleaned : cleaned.slice(0, dotIndex);
  const tld = dotIndex === -1 ? '' : cleaned.slice(dotIndex + 1);
  const candidateTlds = ['co.za', 'com', 'africa', 'net', 'org.za'].filter((candidate) => candidate !== tld);
  return Array.from(new Set(candidateTlds.slice(0, 3).map((candidate) => `${sld}.${candidate}`)));
};

const uniqueIds = (ids: string[]) => Array.from(new Set(ids));

const getInitialPackageDecision = (planId: ProductTierId, items: ProductCatalogItem[]): PackageDecision => ({
  summary: 'Waiting for the first answer. The package will sharpen as the brief becomes clearer.',
  confirmedIds: items.filter((item) => item.included_tiers.includes(planId)).map((item) => item.id),
  recommendedIds: [],
  questionIds: [],
  questions: [],
  confidence: 0.25,
});

const catalogSignals: Record<string, string[]> = {
  domain_hosting: ['domain', 'hosting', 'host', 'dns', 'register', 'registrar', 'buy domain', 'purchase domain', 'website address'],
  contact_form: ['contact', 'form', 'enquiry', 'inquiry', 'quote', 'lead', 'message', 'booking', 'book'],
  payment_portal: ['payment', 'pay', 'checkout', 'subscription', 'deposit', 'invoice payment', 'card', 'yoco', 'stripe'],
  online_shop_system: ['shop', 'store', 'product', 'products', 'catalog', 'catalogue', 'cart', 'order', 'orders', 'buy', 'purchase'],
  ai_suite: ['ai', 'assistant', 'bot', 'automation', 'automate', 'intelligent', 'mistral', 'llm', 'agent', 'suite'],
  customer_accounts: ['login', 'sign in', 'account', 'member', 'portal', 'profile', 'customer area', 'private'],
  database: ['database', 'backend', 'save', 'records', 'dashboard', 'data', 'admin', 'manage'],
  cms_shop: ['shop', 'store', 'product', 'stock', 'catalog', 'catalogue', 'cms', 'blog', 'articles'],
  social_scheduler: ['social', 'post', 'schedule', 'instagram', 'facebook', 'content'],
  guided_intake: ['assistant', 'guided', 'intake', 'brief', 'automate', 'automation'],
  crm: ['crm', 'client', 'customer', 'lead', 'pipeline', 'follow-up', 'follow up'],
  invoicing: ['invoice', 'invoicing', 'invocing', 'invoce', 'quote', 'billing', 'estimate', 'accounting'],
};

const questionByItemId: Record<string, string> = {
  domain_hosting: 'Does the customer already own a domain, or should we handle buying/registering and connecting it?',
  contact_form: 'Should the site collect enquiries through a polished contact or quote form?',
  payment_portal: 'Will customers need to pay online, place deposits, or buy products/services?',
  online_shop_system: 'Are these simple product orders, or do you need stock, variants, categories, and order management?',
  ai_suite: 'Should the AI Suite help with customer intake, support, internal operations, or sales guidance?',
  customer_accounts: 'Do customers or staff need to log in to a private area?',
  database: 'Does anything need to be saved, managed, searched, or edited after launch?',
  cms_shop: 'Do you need editable products, services, articles, stock, or shop pages?',
  social_scheduler: 'Should the site help plan or schedule social media posts?',
  crm: 'Do you need to track leads, clients, jobs, or follow-ups?',
  invoicing: 'Should quotes or invoices live inside the system?',
};

const inferPackageDecision = (
  planId: ProductTierId,
  items: ProductCatalogItem[],
  messages: DraftMessage[],
): PackageDecision => {
  const initial = getInitialPackageDecision(planId, items);
  const clientText = messages
    .filter((message) => message.role === 'client')
    .map((message) => message.content)
    .join(' ')
    .toLowerCase();
  const confirmedIds = [...initial.confirmedIds];
  const recommendedIds = [...initial.recommendedIds];
  const questionIds: string[] = [];

  items.forEach((item) => {
    const signals = catalogSignals[item.id] || [];
    const hasSignal = signals.some((signal) => clientText.includes(signal));

    if (hasSignal) {
      if (item.included_tiers.includes(planId)) confirmedIds.push(item.id);
      else recommendedIds.push(item.id);
    }
  });

  if (/\b\d+\s*(page|pages|section|sections)\b/.test(clientText) || clientText.includes('simple website')) {
    questionIds.push('domain_hosting', 'contact_form', 'payment_portal', 'customer_accounts');
  }

  if (recommendedIds.some((id) => ['payment_portal', 'customer_accounts', 'cms_shop', 'crm', 'invoicing'].includes(id))) {
    recommendedIds.push('database');
  }

  const cleanedConfirmedIds = uniqueIds(confirmedIds).filter((id) => items.some((item) => item.id === id));
  const hasOnlineShopSystem = recommendedIds.includes('online_shop_system');
  const cleanedRecommendedIds = uniqueIds(recommendedIds)
    .filter((id) => !hasOnlineShopSystem || !['payment_portal', 'database', 'cms_shop'].includes(id))
    .filter((id) => !cleanedConfirmedIds.includes(id))
    .filter((id) => items.some((item) => item.id === id));
  const cleanedQuestionIds = uniqueIds(questionIds)
    .filter((id) => !cleanedConfirmedIds.includes(id) && !cleanedRecommendedIds.includes(id))
    .filter((id) => items.some((item) => item.id === id))
    .slice(0, 4);
  const pageMatch = clientText.match(/\b(\d+)\s*(page|pages|section|sections)\b/);
  const summary = pageMatch
    ? `${pageMatch[1]} ${pageMatch[2]} captured. Now we are checking whether the build needs enquiries, payments, accounts, or backend tools.`
    : clientText
      ? 'Initial brief captured. The package is now sorting must-haves from smart add-ons.'
      : initial.summary;

  return {
    summary,
    confirmedIds: cleanedConfirmedIds,
    recommendedIds: cleanedRecommendedIds,
    questionIds: cleanedQuestionIds,
    questions: cleanedQuestionIds.map((id) => questionByItemId[id]).filter(Boolean).slice(0, 3),
    confidence: Math.min(0.9, 0.3 + messages.filter((message) => message.role === 'client').length * 0.095),
  };
};

const getChatSuggestionItems = (
  planId: ProductTierId,
  decision: PackageDecision,
  selectedIds: string[],
  items: ProductCatalogItem[],
): ProductCatalogItem[] => {
  const candidateIds = uniqueIds([...decision.recommendedIds, ...decision.questionIds]);
  const result: ProductCatalogItem[] = [];
  for (const id of candidateIds) {
    const item = items.find((entry) => entry.id === id);
    if (
      item &&
      item.active &&
      !item.included_tiers.includes(planId) &&
      !selectedIds.includes(id)
    ) {
      result.push(item);
    }
    if (result.length >= 4) break;
  }
  return result;
};

const mergePreviewDecision = (
  fallbackDecision: PackageDecision,
  previewPackage: ProductPreviewResult['package'],
): PackageDecision => ({
  summary: previewPackage?.summary || fallbackDecision.summary,
  confirmedIds: uniqueIds([...(fallbackDecision.confirmedIds || []), ...(previewPackage?.confirmedItemIds || [])]),
  recommendedIds: uniqueIds([...(fallbackDecision.recommendedIds || []), ...(previewPackage?.recommendedItemIds || [])]),
  questionIds: uniqueIds([...(fallbackDecision.questionIds || []), ...(previewPackage?.questionItemIds || [])]),
  questions: previewPackage?.questions?.length ? previewPackage.questions : fallbackDecision.questions,
  confidence: typeof previewPackage?.confidence === 'number' ? previewPackage.confidence : fallbackDecision.confidence,
});

const getInitialSelectedIds = (planId: ProductTierId, items: ProductCatalogItem[]) =>
  items.filter((item) => item.included_tiers.includes(planId)).map((item) => item.id);

const ensureDomainCatalogItem = (items: ProductCatalogItem[]) => {
  if (items.some((item) => item.id === 'domain_hosting')) return items;

  const domainItem = fallbackCatalogItems.find((item) => item.id === 'domain_hosting');
  return domainItem ? [...items, domainItem].sort((a, b) => a.position - b.position) : items;
};

const ADDON_CARD_WIDTH = 232;
const ADDON_CARD_GAP = 10;

type ManualAddonSwatchButtonProps = {
  item: ProductCatalogItem;
  planId: ProductTierId;
  isSelected: boolean;
  onToggle: (planId: ProductTierId, item: ProductCatalogItem) => void;
  isLightTheme: boolean;
};

/** Circular control — portfolio-style animated stroke (PillChip / bottom swatch pattern), round. */
const ManualAddonSwatchButton: React.FC<ManualAddonSwatchButtonProps> = ({
  item,
  planId,
  isSelected,
  onToggle,
  isLightTheme,
}) => {
  const accent = isLightTheme ? '#0f766e' : '#f97316';
  const cardBg = isLightTheme ? 'rgba(255,255,255,0.72)' : 'rgba(10,10,10,0.55)';
  const cardBorder = isLightTheme ? 'rgba(15,118,110,0.22)' : 'rgba(251,146,60,0.22)';
  const labelColor = isLightTheme ? 'rgba(6,19,26,0.78)' : 'rgba(255,247,237,0.72)';

  return (
    <div className="flex w-[5.25rem] shrink-0 flex-col items-center gap-1.5 self-start">
      <motion.button
        type="button"
        whileHover="hover"
        onClick={(event) => {
          event.stopPropagation();
          onToggle(planId, item);
        }}
        title={item.label}
        aria-label={isSelected ? `Remove ${item.label} from package` : `Add ${item.label} to package`}
        className={`relative flex h-9 w-9 shrink-0 items-center justify-center overflow-visible rounded-full border transition-colors duration-300 ${
          isSelected ? 'opacity-100' : 'opacity-90 hover:opacity-100'
        }`}
        style={{
          background: cardBg,
          borderColor: isSelected ? accent : cardBorder,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        <span className="relative z-[1] text-[15px] font-black leading-none" style={{ color: accent }}>
          {isSelected ? '✓' : '+'}
        </span>
        <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 36 36" aria-hidden>
          <motion.circle
            cx="18"
            cy="18"
            r="16.25"
            fill="none"
            stroke={accent}
            strokeWidth={1.5}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            variants={{
              hover: {
                pathLength: 1,
                opacity: 1,
                transition: {
                  pathLength: { duration: 0.55, ease: [0.32, 0.72, 0.4, 1] },
                  opacity: { duration: 0.15 },
                },
              },
            }}
            style={{ filter: `drop-shadow(0 0 6px ${accent})` }}
          />
        </svg>
      </motion.button>
      <span
        className="line-clamp-3 w-full break-words text-center text-[8px] font-bold leading-tight tracking-[0.04em] sm:text-[9px] sm:tracking-wide"
        style={{ color: labelColor }}
      >
        {item.label}
      </span>
    </div>
  );
};

const getManualQuickPickAddons = (planId: ProductTierId, visibleItems: ProductCatalogItem[]) =>
  visibleItems
    .filter(
      (item) =>
        item.active &&
        item.customer_visible &&
        !item.included_tiers.includes(planId),
    )
    .sort((a, b) => a.position - b.position)
    .slice(0, 5);

type FeatureAddonsCarouselProps = {
  items: PricedCatalogItem[];
  planId: ProductTierId;
  decision: PackageDecision;
  selectedIds: string[];
  domainPreference: DomainPreference | null;
  billingMode: BillingMode;
  onToggleItem: (planId: ProductTierId, item: ProductCatalogItem) => void;
  isLightTheme: boolean;
};

const FeatureAddonsCarousel: React.FC<FeatureAddonsCarouselProps> = ({
  items,
  planId,
  decision,
  selectedIds,
  domainPreference,
  billingMode,
  onToggleItem,
  isLightTheme,
}) => {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 6);
    setCanNext(el.scrollLeft < max - 6);
  }, []);

  React.useLayoutEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => updateScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [items.length, updateScrollState]);

  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = ADDON_CARD_WIDTH + ADDON_CARD_GAP;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const arrowRing = isLightTheme
    ? 'border-teal-400/40 bg-white/30 text-teal-900 shadow-[0_4px_24px_rgba(20,184,166,0.18)] hover:border-teal-500/55 hover:bg-white/50'
    : 'border-orange-300/35 bg-black/55 text-orange-100 shadow-[0_4px_24px_rgba(249,115,22,0.15)] hover:border-orange-300/60 hover:bg-black/70';

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-3">
        <p className="py-2 text-xs leading-5 text-orange-100/45">Add-ons will appear here as the consult detects them.</p>
      </div>
    );
  }

  return (
    <div
      className="relative min-w-0 w-full max-w-full overflow-hidden rounded-xl border border-white/10 bg-black/20"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className={`pointer-events-none absolute inset-y-2 left-0 z-[1] w-12 rounded-l-[10px] bg-gradient-to-r to-transparent ${
          isLightTheme ? 'from-[#f1f5f9]/95' : 'from-[#070707]/95'
        }`}
        aria-hidden
      />
      <div
        className={`pointer-events-none absolute inset-y-2 right-0 z-[1] w-12 rounded-r-[10px] bg-gradient-to-l to-transparent ${
          isLightTheme ? 'from-[#f1f5f9]/95' : 'from-[#070707]/95'
        }`}
        aria-hidden
      />

      <button
        type="button"
        aria-label="Scroll add-ons left"
        disabled={!canPrev}
        onClick={(event) => {
          event.stopPropagation();
          scrollByDir(-1);
        }}
        className={`absolute left-1.5 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border backdrop-blur-md transition-all disabled:pointer-events-none disabled:opacity-[0.22] ${arrowRing}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden className="-ml-0.5">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Scroll add-ons right"
        disabled={!canNext}
        onClick={(event) => {
          event.stopPropagation();
          scrollByDir(1);
        }}
        className={`absolute right-1.5 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border backdrop-blur-md transition-all disabled:pointer-events-none disabled:opacity-[0.22] ${arrowRing}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden className="-mr-0.5">
          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div
        ref={scrollerRef}
        className="flex min-h-0 min-w-0 w-full max-w-full snap-x snap-proximity items-stretch gap-2.5 overflow-x-auto overflow-y-hidden scroll-smooth px-11 py-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ gap: ADDON_CARD_GAP }}
      >
        {items.map((item, index) => {
          const itemPrice = item.adjusted_price;
          const needsAnswer = decision.questionIds.includes(item.id);
          const isSelected = selectedIds.includes(item.id);
          const isRecommended = decision.recommendedIds.includes(item.id);

          const removeAddon = (event: React.MouseEvent) => {
            event.stopPropagation();
            onToggleItem(planId, item);
          };
          const addAddon = (event: React.SyntheticEvent) => {
            event.stopPropagation();
            if (!isSelected) onToggleItem(planId, item);
          };

          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03, duration: 0.2, ease: 'easeOut' }}
              role={isSelected ? undefined : 'button'}
              tabIndex={isSelected ? undefined : 0}
              onClick={isSelected ? undefined : addAddon}
              onKeyDown={
                isSelected
                  ? undefined
                  : (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onToggleItem(planId, item);
                      }
                    }
              }
              className={`relative box-border flex shrink-0 snap-start flex-col overflow-hidden rounded-xl border p-1.5 text-left transition-colors ${
                isSelected
                  ? 'border-orange-300/35 bg-orange-500/[0.08] shadow-[inset_0_1px_0_rgba(251,146,60,0.12)]'
                  : 'cursor-pointer border-white/10 bg-black/25 hover:border-orange-300/35 hover:bg-orange-500/[0.04]'
              }`}
              style={{
                width: ADDON_CARD_WIDTH,
                minWidth: ADDON_CARD_WIDTH,
                maxWidth: ADDON_CARD_WIDTH,
                minHeight: 64,
                flex: `0 0 ${ADDON_CARD_WIDTH}px`,
              }}
            >
              {isSelected ? (
                <button
                  type="button"
                  onClick={removeAddon}
                  className="absolute right-0.5 top-0.5 z-10 flex h-[1.264rem] w-[1.264rem] items-center justify-center rounded-full border border-orange-400/70 bg-orange-400 text-black shadow-[0_0_10px_rgba(251,146,60,0.38)] transition-colors hover:bg-orange-300"
                  aria-label={`Remove ${item.label}`}
                >
                  <span className="translate-y-[-0.5px] text-[10.8px] font-black leading-none">×</span>
                </button>
              ) : null}

              <div className={`relative flex min-h-0 flex-1 flex-row items-center gap-1.5 ${isSelected ? 'pr-4' : ''}`}>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-0.5">
                  <div className="min-h-0 space-y-0">
                    <span className="line-clamp-2 text-[11px] font-black leading-tight text-orange-300 transition-colors">
                      {item.label}
                    </span>
                    <span className="block text-[8px] font-bold uppercase leading-none tracking-[0.12em] text-orange-100/40">
                      {isSelected
                        ? 'selected add-on'
                        : isRecommended
                          ? 'recommended'
                          : needsAnswer
                            ? 'consulting'
                            : item.category}
                    </span>
                  </div>
                  {item.discount_amount > 0 ? (
                    <span className="text-[7px] font-black uppercase leading-tight tracking-[0.08em] text-green-400/95">
                      Shared backend discount
                    </span>
                  ) : null}
                  {item.id === 'domain_hosting' && domainPreference?.needsRegistration ? (
                    <span className="line-clamp-1 text-[10px] font-bold leading-tight normal-case tracking-normal text-orange-100/72">
                      {domainPreference.desiredDomain || 'Domain pending'}
                    </span>
                  ) : null}
                  {item.id === 'domain_hosting' && isSelected ? (
                    <span className="flex items-center gap-1 text-[8px] font-black uppercase leading-none tracking-[0.12em] text-orange-200/70">
                      {domainPreference?.availabilityStatus === 'available' ? (
                        <>
                          <span className="h-1 w-1 shrink-0 rounded-full bg-green-300" />
                          Ready
                        </>
                      ) : (
                        <>
                          <span className="h-2 w-2 shrink-0 animate-spin rounded-full border border-orange-300/30 border-t-orange-300" />
                          Check
                        </>
                      )}
                    </span>
                  ) : null}
                </div>

                <div className="flex w-[3.875rem] shrink-0 flex-col items-end justify-center self-stretch text-right">
                  {itemPrice > 0 ? (
                    <>
                      {item.discount_amount > 0 ? (
                        <span className="mb-0 block text-[8px] font-bold leading-none text-orange-100/30 line-through">
                          {formatRand(item.original_price)}
                          {billingMode === 'monthly' ? '/m' : ''}
                        </span>
                      ) : null}
                      <span
                        className={`text-[16px] font-black leading-none tracking-tight sm:text-[17px] ${
                          item.discount_amount > 0 ? 'text-green-400' : 'text-orange-400'
                        }`}
                        style={{
                          textShadow:
                            item.discount_amount > 0
                              ? '0 0 12px rgba(74, 222, 128, 0.32), 0 0 22px rgba(74, 222, 128, 0.1)'
                              : '0 0 14px rgba(251, 146, 60, 0.38), 0 0 28px rgba(251, 146, 60, 0.1)',
                        }}
                      >
                        {formatRand(itemPrice)}
                      </span>
                      {billingMode === 'monthly' ? (
                        <span
                          className={`mt-0 block text-[9px] font-bold uppercase leading-none tracking-[0.1em] ${
                            item.discount_amount > 0 ? 'text-green-200/75' : 'text-orange-200/65'
                          }`}
                        >
                          per month
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span
                      className="text-sm font-black leading-none text-orange-100/50"
                      style={{ textShadow: '0 0 10px rgba(251, 146, 60, 0.18)' }}
                    >
                      Included
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const ProductPage = () => {
  const { theme } = useTheme();
  const isLightTheme = theme === 'light';
  const [billingMode, setBillingMode] = React.useState<BillingMode>('monthly');
  const [activePlanId, setActivePlanId] = React.useState<ProductTierId | null>(null);
  const [chatInput, setChatInput] = React.useState('');
  const [isClientLoggedIn, setIsClientLoggedIn] = React.useState(false);
  const [catalogItems, setCatalogItems] = React.useState<ProductCatalogItem[]>(fallbackCatalogItems);
  const [selectedCatalogIds, setSelectedCatalogIds] = React.useState<Record<ProductTierId, string[]>>({
    launch: getInitialSelectedIds('launch', fallbackCatalogItems),
    growth: getInitialSelectedIds('growth', fallbackCatalogItems),
    command: getInitialSelectedIds('command', fallbackCatalogItems),
  });
  const [packageDecisions, setPackageDecisions] = React.useState<Record<ProductTierId, PackageDecision>>({
    launch: getInitialPackageDecision('launch', fallbackCatalogItems),
    growth: getInitialPackageDecision('growth', fallbackCatalogItems),
    command: getInitialPackageDecision('command', fallbackCatalogItems),
  });
  const [draftMessages, setDraftMessages] = React.useState<Record<ProductTierId, DraftMessage[]>>({
    launch: [{ role: 'assistant', content: firstQuestionByTier.launch }],
    growth: [{ role: 'assistant', content: firstQuestionByTier.growth }],
    command: [{ role: 'assistant', content: firstQuestionByTier.command }],
  });
  const [isDraftThinking, setIsDraftThinking] = React.useState<Record<ProductTierId, boolean>>({
    launch: false,
    growth: false,
    command: false,
  });
  const [domainPreferences, setDomainPreferences] = React.useState<Record<ProductTierId, DomainPreference | null>>({
    launch: null,
    growth: null,
    command: null,
  });
  const [domainModalPlanId, setDomainModalPlanId] = React.useState<ProductTierId | null>(null);
  const [lastBriefMessagePlanId, setLastBriefMessagePlanId] = React.useState<ProductTierId | null>(null);
  const pendingBriefMessageRef = React.useRef('');
  const [domainInput, setDomainInput] = React.useState('');
  const [domainModalError, setDomainModalError] = React.useState('');
  const [isDomainChecking, setIsDomainChecking] = React.useState(false);
  const [domainCheckResult, setDomainCheckResult] = React.useState<DomainCheckResult | null>(null);
  const draftReplyTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    const root = document.documentElement;
    document.body.classList.add('product-page-active');
    const keys = ['--text-primary', '--text-secondary', '--accent-primary', '--accent-secondary', '--border-primary'];
    const previousValues = keys.reduce<Record<string, string>>((values, key) => {
      values[key] = root.style.getPropertyValue(key);
      return values;
    }, {});

    root.style.setProperty('--text-primary', isLightTheme ? '#06131a' : '#fff7ed');
    root.style.setProperty('--text-secondary', isLightTheme ? 'rgba(8,47,73,0.72)' : '#fed7aa');
    root.style.setProperty('--accent-primary', isLightTheme ? '#0f766e' : '#f97316');
    root.style.setProperty('--accent-secondary', isLightTheme ? '#38bdf8' : '#f59e0b');
    root.style.setProperty('--border-primary', isLightTheme ? 'rgba(20,184,166,0.34)' : 'rgba(251,146,60,0.32)');

    return () => {
      document.body.classList.remove('product-page-active');
      keys.forEach((key) => {
        if (previousValues[key]) {
          root.style.setProperty(key, previousValues[key]);
        } else {
          root.style.removeProperty(key);
        }
      });
    };
  }, [isLightTheme]);

  React.useEffect(() => {
    return () => {
      if (draftReplyTimer.current) {
        clearTimeout(draftReplyTimer.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!isSupabaseConfigured) return;

    supabase.auth.getSession().then(({ data }) => {
      const user = data.session?.user;
      setIsClientLoggedIn(Boolean(user));
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user;
      setIsClientLoggedIn(Boolean(user));
    });

    return () => subscription.unsubscribe();
  }, []);

  React.useEffect(() => {
    const loadCatalog = async () => {
      if (!isSupabaseConfigured) return;

      const { data, error } = await supabase
        .from('product_catalog_items')
        .select('*')
        .eq('active', true)
        .eq('customer_visible', true)
        .order('position', { ascending: true });

      if (error || !data?.length) return;

      const nextItems = ensureDomainCatalogItem(data as ProductCatalogItem[]);
      setCatalogItems(nextItems);
      setSelectedCatalogIds({
        launch: getInitialSelectedIds('launch', nextItems),
        growth: getInitialSelectedIds('growth', nextItems),
        command: getInitialSelectedIds('command', nextItems),
      });
      setPackageDecisions({
        launch: getInitialPackageDecision('launch', nextItems),
        growth: getInitialPackageDecision('growth', nextItems),
        command: getInitialPackageDecision('command', nextItems),
      });
    };

    loadCatalog();
  }, []);

  const activeMessages = activePlanId ? draftMessages[activePlanId] : [];
  const activeClientCount = activeMessages.filter((message) => message.role === 'client').length;
  const isIntakeComplete = Boolean(activePlanId) && activeClientCount >= INTAKE_TOTAL_QUESTIONS;

  const getVisibleCatalogItems = (planId: ProductTierId) => {
    const selectedIds = selectedCatalogIds[planId] || [];
    const decision = packageDecisions[planId];

    return catalogItems.filter(
      (item) =>
        item.included_tiers.includes(planId) ||
        item.suggested_tiers.includes(planId) ||
        item.category === 'standard' ||
        item.id === 'online_shop_system' ||
        selectedIds.includes(item.id) ||
        decision?.recommendedIds.includes(item.id) ||
        decision?.questionIds.includes(item.id),
    );
  };

  const buildCartForPlan = (planId: ProductTierId) => {
    const plan = productPlans.find((item) => item.id === planId);
    if (!plan) return null;

    const price = billingMode === 'monthly' ? plan.monthlyPrice : plan.buyoutPrice;
    const visibleCatalogItems = getVisibleCatalogItems(planId);
    const selectedAddOnItems = visibleCatalogItems.filter(
      (item) => !item.included_tiers.includes(planId) && selectedCatalogIds[planId].includes(item.id),
    );
    const addOnPricing = calculateAddOnPricing(selectedAddOnItems, billingMode);
    const addOns = addOnPricing.items.map((item) => ({
      id: item.id,
      label: item.label,
      price: item.adjusted_price,
      originalPrice: item.original_price,
      discountAmount: item.discount_amount,
      discountLabel: item.discount_label,
    }));
    const messages = draftMessages[planId] || [];
    const domainPreference = domainPreferences[planId];
    const domainRegistrationTotal =
      domainPreference?.needsRegistration && domainPreference.availabilityStatus === 'available'
        ? domainPreference.purchaseAmount || 0
        : 0;
    const pricing: PricingSnapshot = {
      basePrice: price || 0,
      addOnOriginalTotal: addOnPricing.originalTotal,
      addOnDiscountTotal: addOnPricing.discountTotal,
      addOnTotal: addOnPricing.discountedTotal,
      domainRegistrationTotal,
      total: (price || 0) + addOnPricing.discountedTotal + domainRegistrationTotal,
      items: addOnPricing.items.map((item) => ({
        id: item.id,
        label: item.label,
        originalPrice: item.original_price,
        adjustedPrice: item.adjusted_price,
        discountAmount: item.discount_amount,
        discountLabel: item.discount_label,
      })),
    };

    return {
      planId: plan.id,
      planName: plan.name,
      billingMode,
      basePrice: price || 0,
      addOns,
      total: pricing.total,
      spec: {
        summary: packageDecisions[planId].summary,
        messages,
        domain: domainPreference
          ? {
              needsRegistration: domainPreference.needsRegistration,
              desiredDomain: domainPreference.desiredDomain,
              availabilityStatus: domainPreference.availabilityStatus,
              availabilityMode: domainPreference.availabilityMode,
              availabilityMessage: domainPreference.availabilityMessage,
              isAvailable: domainPreference.isAvailable,
              isPremium: domainPreference.isPremium,
              premiumReason: domainPreference.premiumReason,
              purchaseAmount: domainPreference.purchaseAmount,
            }
          : null,
        pricing,
      },
    };
  };

  const addCurrentPlanToCart = (planId: ProductTierId, openCart = true) => {
    const cart = buildCartForPlan(planId);
    if (!cart) return;

    window.dispatchEvent(new CustomEvent('ddd:update-cart', { detail: cart }));
    if (openCart) {
      window.dispatchEvent(new CustomEvent('ddd:open-cart'));
    }
  };

  React.useEffect(() => {
    const handleCartUpdated = (event: Event) => {
      const cart = (event as CustomEvent).detail;
      if (!activePlanId || !cart || cart.planId !== activePlanId) return;

      setSelectedCatalogIds((current) => {
        const includedIds = getInitialSelectedIds(activePlanId, catalogItems);
        const nextIds = uniqueIds([...includedIds, ...(cart.addOns || []).map((addon: { id: string }) => addon.id)]);
        if (current[activePlanId].join('|') === nextIds.join('|')) return current;

        return {
          ...current,
          [activePlanId]: nextIds,
        };
      });
    };

    window.addEventListener('ddd:update-cart', handleCartUpdated);
    return () => window.removeEventListener('ddd:update-cart', handleCartUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlanId, catalogItems]);

  const toggleCatalogItem = (planId: ProductTierId, item: ProductCatalogItem) => {
    if (item.included_tiers.includes(planId)) return;

    setSelectedCatalogIds((current) => {
      const selected = current[planId];
      const nextSelected = selected.includes(item.id)
        ? selected.filter((id) => id !== item.id)
        : [...selected, item.id];

      return {
        ...current,
        [planId]: nextSelected,
      };
    });
  };

  const handleCardOpen = (planId: ProductTierId) => {
    setActivePlanId((current) => (current === planId ? null : planId));
    setChatInput('');
  };

  const handleResetConsult = (planId: ProductTierId) => {
    if (draftReplyTimer.current) {
      clearTimeout(draftReplyTimer.current);
      draftReplyTimer.current = null;
    }

    setLastBriefMessagePlanId(null);
    pendingBriefMessageRef.current = '';
    setChatInput('');
    setDraftMessages((current) => ({
      ...current,
      [planId]: [{ role: 'assistant', content: firstQuestionByTier[planId] }],
    }));
    setPackageDecisions((current) => ({
      ...current,
      [planId]: getInitialPackageDecision(planId, catalogItems),
    }));
    setSelectedCatalogIds((current) => ({
      ...current,
      [planId]: getInitialSelectedIds(planId, catalogItems),
    }));
    setDomainPreferences((current) => ({
      ...current,
      [planId]: null,
    }));
    setIsDraftThinking((current) => ({
      ...current,
      [planId]: false,
    }));
  };

  const openDomainModal = (planId: ProductTierId) => {
    setDomainInput(domainPreferences[planId]?.desiredDomain || '');
    setDomainModalError('');
    setDomainCheckResult(null);
    setDomainModalPlanId(planId);
  };

  const closeDomainModal = () => {
    setDomainModalPlanId(null);
    setDomainInput('');
    setDomainModalError('');
    setDomainCheckResult(null);
    setIsDomainChecking(false);
  };

  const runDomainCheck = async (planId: ProductTierId, rawInput: string) => {
    const cleanedDomain = rawInput.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '').toLowerCase();
    if (!cleanedDomain) {
      setDomainModalError('Type a domain first, like example.co.za');
      setDomainCheckResult(null);
      return;
    }

    setIsDomainChecking(true);
    setDomainModalError('');

    const result = await checkDesiredDomain(cleanedDomain);
    setIsDomainChecking(false);
    setDomainCheckResult(result);

    setDomainPreferences((current) => ({
      ...current,
      [planId]: {
        needsRegistration: true,
        desiredDomain: result.domain || cleanedDomain,
        availabilityStatus: result.availabilityStatus || 'check_failed',
        availabilityMode: result.mode,
        availabilityMessage: result.message,
        isAvailable: typeof result.isAvailable === 'boolean' ? result.isAvailable : null,
        isPremium: typeof result.isPremium === 'boolean' ? result.isPremium : null,
        premiumReason: result.reason || '',
        purchaseAmount: result.purchaseAmount || 0,
      },
    }));
  };

  const tryDifferentDomain = (suggested?: string) => {
    if (suggested) setDomainInput(suggested);
    setDomainCheckResult(null);
    setDomainModalError('');
  };

  const addDomainToCart = (planId: ProductTierId) => {
    const result = domainCheckResult;
    const desiredDomain = result?.domain || domainInput.trim().toLowerCase();
    if (!desiredDomain) return;

    setDomainPreferences((current) => ({
      ...current,
      [planId]: {
        needsRegistration: true,
        desiredDomain,
        availabilityStatus: result?.availabilityStatus || 'manual',
        availabilityMode: result?.mode,
        availabilityMessage: result?.message,
        isAvailable: typeof result?.isAvailable === 'boolean' ? result.isAvailable : null,
        isPremium: typeof result?.isPremium === 'boolean' ? result.isPremium : null,
        premiumReason: result?.reason || '',
        purchaseAmount: result?.purchaseAmount || 0,
      },
    }));
    setSelectedCatalogIds((current) => ({
      ...current,
      [planId]: uniqueIds([...current[planId], 'domain_hosting']),
    }));
    setPackageDecisions((current) => ({
      ...current,
      [planId]: {
        ...current[planId],
        recommendedIds: uniqueIds([...current[planId].recommendedIds, 'domain_hosting']),
        summary: `${current[planId].summary} Desired domain captured: ${desiredDomain}.`,
      },
    }));
    closeDomainModal();
  };

  const skipDomainStep = (planId: ProductTierId) => {
    setDomainPreferences((current) => ({
      ...current,
      [planId]: {
        needsRegistration: false,
        desiredDomain: '',
      },
    }));
    setSelectedCatalogIds((current) => ({
      ...current,
      [planId]: current[planId].filter((id) => id !== 'domain_hosting'),
    }));
    setPackageDecisions((current) => ({
      ...current,
      [planId]: {
        ...current[planId],
        recommendedIds: current[planId].recommendedIds.filter((id) => id !== 'domain_hosting'),
        summary: `${current[planId].summary} Customer will handle their own domain.`,
      },
    }));
    closeDomainModal();
  };

  const runDraftSendPipeline = (planId: ProductTierId, messageText: string) => {
    const trimmed = messageText.trim();
    if (!trimmed) return;

    const existing = draftMessages[planId];
    const existingClientCount = existing.filter((message) => message.role === 'client').length;
    const clientMessage: DraftMessage = { role: 'client', content: trimmed };
    const nextClientMessages = [...existing, clientMessage];
    const nextClientCount = existingClientCount + 1;

    const isFirstReply = existingClientCount === 0;
    const alreadyCapturedDomain = Boolean(domainPreferences[planId]);
    const shouldRunDomainDetection = isFirstReply && !alreadyCapturedDomain;
    const shouldCollectDesiredDomain =
      shouldRunDomainDetection && wantsDomainRegistration(clientMessage.content, existing);
    const customerHasDomain =
      shouldRunDomainDetection && hasExistingDomain(clientMessage.content, existing);

    setDraftMessages((current) => ({
      ...current,
      [planId]: nextClientMessages,
    }));
    if (shouldCollectDesiredDomain) {
      setSelectedCatalogIds((current) => ({
        ...current,
        [planId]: uniqueIds([...current[planId], 'domain_hosting']),
      }));
      setPackageDecisions((current) => ({
        ...current,
        [planId]: {
          ...current[planId],
          recommendedIds: uniqueIds([...current[planId].recommendedIds, 'domain_hosting']),
          questions: uniqueIds([
            'Which web address would you like us to try register for you?',
            ...current[planId].questions,
          ]).slice(0, 3),
        },
      }));
      window.setTimeout(() => openDomainModal(planId), 250);
    } else if (customerHasDomain) {
      setDomainPreferences((current) => ({
        ...current,
        [planId]: {
          needsRegistration: false,
          desiredDomain: '',
        },
      }));
    }
    setIsDraftThinking((current) => ({
      ...current,
      [planId]: true,
    }));

    if (draftReplyTimer.current) {
      clearTimeout(draftReplyTimer.current);
    }

    const replyDelay = Math.min(1400, 700 + clientMessage.content.length * 14);
    draftReplyTimer.current = setTimeout(async () => {
      let previewResult: ProductPreviewResult | null = null;

      try {
        const response = await fetch('/api/product-intake-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            planId,
            messages: nextClientMessages,
            catalogItems,
          }),
        });

        if (response.ok) {
          previewResult = await response.json();
        }
      } catch {
        previewResult = null;
      }

      const fromApi = previewResult?.reply != null ? String(previewResult.reply).trim() : '';
      const assistantBody =
        fromApi ||
        buildScriptedReply(nextClientCount) ||
        'I have captured that. If anything else matters for scope, mention it.';

      const assistantMessage: DraftMessage = {
        role: 'assistant',
        content: assistantBody,
      };
      const nextMessages = [...nextClientMessages, assistantMessage];
      const fallbackDecision = inferPackageDecision(planId, catalogItems, nextMessages);
      const nextDecision = removeUnrequestedAiSuite(
        mergePreviewDecision(fallbackDecision, previewResult?.package),
        nextMessages,
      );

      setDraftMessages((current) => ({
        ...current,
        [planId]: nextMessages,
      }));
      setPackageDecisions((current) => ({
        ...current,
        [planId]: nextDecision,
      }));
      setSelectedCatalogIds((current) => {
        const includedIds = catalogItems.filter((item) => item.included_tiers.includes(planId)).map((item) => item.id);
        return {
          ...current,
          [planId]: uniqueIds([...current[planId], ...includedIds, ...nextDecision.recommendedIds]),
        };
      });
      setIsDraftThinking((current) => ({
        ...current,
        [planId]: false,
      }));
    }, replyDelay);
  };

  const handleDraftSend = () => {
    if (!activePlanId || !chatInput.trim() || isDraftThinking[activePlanId]) return;

    if (!isClientLoggedIn) {
      window.dispatchEvent(new CustomEvent('ddd:open-auth', { detail: { mode: 'login' } }));
      return;
    }

    const planId = activePlanId;
    const existing = draftMessages[planId];
    const existingClientCount = existing.filter((message) => message.role === 'client').length;
    if (existingClientCount >= INTAKE_TOTAL_QUESTIONS) {
      setChatInput('');
      return;
    }

    if (existingClientCount === INTAKE_TOTAL_QUESTIONS - 1) {
      pendingBriefMessageRef.current = chatInput.trim();
      setLastBriefMessagePlanId(planId);
      return;
    }

    const outgoing = chatInput.trim();
    setChatInput('');
    runDraftSendPipeline(planId, outgoing);
  };

  const confirmLastBriefMessageSend = () => {
    const planId = lastBriefMessagePlanId;
    const text = pendingBriefMessageRef.current.trim();
    setLastBriefMessagePlanId(null);
    pendingBriefMessageRef.current = '';
    if (!planId || !text || isDraftThinking[planId]) return;
    setChatInput('');
    runDraftSendPipeline(planId, text);
  };

  const cancelLastBriefMessageSend = () => {
    setLastBriefMessagePlanId(null);
    pendingBriefMessageRef.current = '';
  };

  return (
    <main
      className="product-page-shell relative min-h-screen w-full overflow-hidden px-4 pb-16 pt-24 text-white md:px-6 md:pt-28"
      style={{
        background: isLightTheme
          ? 'radial-gradient(circle at 75% 10%, rgba(20,184,166,0.24), transparent 34%), radial-gradient(circle at 18% 78%, rgba(56,189,248,0.14), transparent 30%), linear-gradient(135deg, #f8fafc 0%, #eefafa 48%, #f7fbfb 100%)'
          : 'radial-gradient(circle at 75% 10%, rgba(249,115,22,0.24), transparent 34%), linear-gradient(135deg, #050505 0%, #0b0b0d 48%, #17100b 100%)',
        ['--accent-primary' as string]: isLightTheme ? '#0f766e' : '#f97316',
        ['--accent-secondary' as string]: isLightTheme ? '#38bdf8' : '#f59e0b',
        ['--text-primary' as string]: isLightTheme ? '#06131a' : '#fff7ed',
        ['--text-secondary' as string]: isLightTheme ? 'rgba(8,47,73,0.72)' : '#fed7aa',
        ['--border-primary' as string]: isLightTheme ? 'rgba(20,184,166,0.28)' : 'rgba(251,146,60,0.28)',
        ['--card-bg' as string]: isLightTheme ? 'rgba(255,255,255,0.78)' : 'rgba(10,10,12,0.72)',
      }}
    >
      <div
        role="status"
        aria-live="polite"
        className={`pointer-events-none fixed right-3 top-20 z-[100] max-w-[200px] rounded-xl border px-3 py-2 text-right shadow-lg backdrop-blur-md md:right-5 md:top-24 md:max-w-[240px] md:px-3.5 md:py-2.5 ${
          isLightTheme
            ? 'border-teal-400/40 bg-white/85 text-teal-950'
            : 'border-orange-300/35 bg-black/75 text-orange-50'
        }`}
      >
        <p className="text-[10px] font-black uppercase tracking-[0.18em] opacity-90">Dev mode</p>
        <p className="mt-0.5 text-[11px] font-semibold leading-snug opacity-85">Coming soon — live testing only.</p>
      </div>

      <Spotlight className="-top-36 left-0" fill={isLightTheme ? '#14b8a6' : '#fb923c'} />

      <section id="plans" className="relative z-10 mx-auto max-w-7xl">
        <div className="mb-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-orange-300">
              Design<span style={{ color: 'var(--accent-primary)' }}>Dot</span>Dev web systems
            </p>
            <h1 className="mt-2 max-w-4xl text-3xl font-black tracking-tight md:text-5xl">
              Bespoke websites, built like products.
            </h1>
            <p className="mt-2 max-w-2xl text-sm font-medium text-orange-100/70 md:text-base">
              Choose the front-end, content engine, or full business platform. We keep the offer clear and the build beautiful.
            </p>
          </div>
          <div className="flex rounded-full border border-white/10 bg-white/10 p-1">
            {(['monthly', 'buyout'] as BillingMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setBillingMode(mode)}
                className={`rounded-full px-5 py-2 text-sm font-bold transition ${
                  billingMode === mode ? 'bg-orange-400 text-black' : 'text-orange-100/70 hover:text-white'
                }`}
              >
                {mode === 'monthly' ? 'Monthly' : 'Buyout'}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {productPlans.map((plan) => {
            const price = billingMode === 'monthly' ? plan.monthlyPrice : plan.buyoutPrice;
            const isActive = activePlanId === plan.id;
            const decision = packageDecisions[plan.id];
            const isThinking = isDraftThinking[plan.id];
            const isBotLocked = !isClientLoggedIn;
            const domainPreference = domainPreferences[plan.id];
            const visibleCatalogItems = getVisibleCatalogItems(plan.id);
            const baseItems = visibleCatalogItems.filter((item) => item.included_tiers.includes(plan.id));
            const selectedAddOnItems = visibleCatalogItems.filter(
              (item) => !item.included_tiers.includes(plan.id) && selectedCatalogIds[plan.id].includes(item.id),
            );
            const featureAddOnItems = selectedAddOnItems
              .sort((a, b) => {
                const getPriority = (item: ProductCatalogItem) => {
                  if (selectedCatalogIds[plan.id].includes(item.id)) return 0;
                  if (decision.recommendedIds.includes(item.id)) return 1;
                  if (decision.questionIds.includes(item.id)) return 2;
                  return 3;
                };

                return getPriority(a) - getPriority(b) || a.position - b.position;
              });
            const addOnPricing = calculateAddOnPricing(featureAddOnItems, billingMode);
            const pricedFeatureAddOnItems = addOnPricing.items;
            const domainRegistrationTotal =
              domainPreference?.needsRegistration && domainPreference.availabilityStatus === 'available'
                ? domainPreference.purchaseAmount || 0
                : 0;
            const checkoutTotal = (price || 0) + addOnPricing.discountedTotal + domainRegistrationTotal;
            const manualQuickPickAddons = getManualQuickPickAddons(plan.id, visibleCatalogItems);
            const clientMsgCount = draftMessages[plan.id]?.filter((m) => m.role === 'client').length ?? 0;
            const messagesLeft = Math.max(0, INTAKE_TOTAL_QUESTIONS - clientMsgCount);
            const chatSuggestions =
              isActive && !isIntakeComplete && !isBotLocked
                ? getChatSuggestionItems(plan.id, decision, selectedCatalogIds[plan.id], catalogItems)
                : [];
            const sharedBackendSavingEl =
              addOnPricing.discountTotal > 0 ? (
                <div>
                  <p className="mb-3 text-[11px] font-black uppercase tracking-[0.22em] text-orange-300">Shared savings</p>
                  <ul className="m-0 list-none space-y-1.5 p-0">
                    <li>
                      <div
                        className={`flex w-full items-start gap-3 rounded-md py-1.5 pl-0.5 pr-2 ${
                          isLightTheme ? 'hover:bg-teal-950/[0.06]' : 'hover:bg-white/[0.04]'
                        }`}
                      >
                        <span
                          className="mt-2 h-1 w-1 shrink-0 rounded-full"
                          style={{
                            backgroundColor: isLightTheme ? '#14b8a6' : '#fb923c',
                            boxShadow: isLightTheme
                              ? '0 0 10px rgba(20,184,166,0.35)'
                              : '0 0 10px rgba(249,115,22,0.35)',
                          }}
                          aria-hidden
                        />
                        <p className="m-0 text-[13px] font-semibold leading-snug text-orange-100/88">
                          <span className="font-black text-orange-300">Shared backend saving:</span>{' '}
                          <span className="font-black text-green-400">
                            {formatRand(addOnPricing.discountTotal)}
                            {billingMode === 'monthly' ? '/m' : ''}
                          </span>{' '}
                          because selected modules share the same Supabase foundation.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              ) : null;

            if (activePlanId && !isActive) {
              return null;
            }

            return (
              <motion.article
                key={plan.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                onClick={() => handleCardOpen(plan.id)}
                className={`group relative cursor-pointer overflow-hidden rounded-[1.5rem] border p-5 backdrop-blur-xl transition-colors duration-300 ${
                  isActive
                    ? 'h-[78vh] min-h-[560px] lg:col-span-3 lg:h-[520px] lg:min-h-0 border-orange-200/80 bg-black/45 shadow-[0_0_52px_rgba(249,115,22,0.18)]'
                    : 'h-[520px]'
                } ${
                  plan.featured && !isActive
                    ? 'border-orange-300/50 bg-[#050505] shadow-[0_0_38px_rgba(249,115,22,0.18)]'
                    : 'border-white/10 bg-[#050505]'
                }`}
              >
                <div
                  className={`pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent to-transparent ${
                    isLightTheme ? 'via-teal-300/75' : 'via-orange-200/70'
                  }`}
                />
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background: isLightTheme
                      ? 'radial-gradient(circle at 50% 0%, rgba(20, 184, 166, 0.2), transparent 36%)'
                      : 'radial-gradient(circle at 50% 0%, rgba(249, 115, 22, 0.18), transparent 36%)',
                  }}
                />
                {plan.featured && !isActive && (
                  <span className="absolute right-5 top-5 rounded-full bg-orange-400 px-3 py-1 text-xs font-black uppercase tracking-widest text-black">
                    Popular
                  </span>
                )}

                {!isActive ? (
                  <div className="relative z-10 flex h-full flex-col items-center justify-center text-center">
                    <p className="mb-8 text-[11px] font-bold uppercase tracking-[0.28em] text-orange-300/80">{plan.eyebrow}</p>
                    <h3 className="text-5xl font-black tracking-tight text-orange-50">{plan.name}</h3>
                    <div className="mt-5">
                      <span className="text-4xl font-black text-white">{formatRand(price)}</span>
                      {billingMode === 'monthly' && plan.termMonths && (
                        <span className="ml-2 text-sm font-bold text-orange-100/55">/month</span>
                      )}
                      {billingMode === 'buyout' && price && (
                        <p className="mt-1 text-xs uppercase tracking-wider text-orange-200/60">
                          {plan.id === 'command' ? '40% once-off saving' : '20% once-off saving'}
                        </p>
                      )}
                    </div>
                    <p className="mt-6 max-w-xs text-sm leading-6 text-orange-100/62">{plan.shortDescription}</p>
                    <div className="mt-10 h-8 overflow-hidden">
                      <span className="block translate-y-4 text-lg font-black uppercase tracking-[0.28em] text-orange-400 opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100">
                        Buy Now
                      </span>
                    </div>
                  </div>
                ) : (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, filter: 'blur(14px)' }}
                    animate={{ opacity: 1, filter: 'blur(0px)' }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}
                    className="relative z-10 flex h-full min-w-0 flex-col gap-0 lg:grid lg:grid-cols-[0.9fr_1.1fr]"
                  >
                    {/* Mobile-only compact spec strip — chat dominates the screen on phones */}
                    <div className="mb-3 flex flex-col gap-2 lg:hidden">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-300/85">
                            {plan.eyebrow}
                          </p>
                          <h2 className="mt-0.5 truncate text-2xl font-black text-orange-50">{plan.name}</h2>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleResetConsult(plan.id);
                            }}
                            className="rounded-full border border-orange-300/35 bg-orange-500/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-orange-200 transition-colors hover:border-orange-200/80 hover:bg-orange-500/20"
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setActivePlanId(null);
                              setChatInput('');
                            }}
                            className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em] text-orange-100 transition-colors hover:border-orange-300/60"
                          >
                            Close
                          </button>
                        </div>
                      </div>

                      <div
                        className="-mx-5 flex snap-x snap-proximity gap-2 overflow-x-auto px-5 pb-1.5 [-webkit-overflow-scrolling:touch] [mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)] [touch-action:pan-x]"
                        style={{ scrollbarWidth: 'none' }}
                        onClick={(event) => event.stopPropagation()}
                        onTouchStart={(event) => event.stopPropagation()}
                      >
                        <div className="snap-start shrink-0 whitespace-nowrap rounded-xl border border-orange-300/45 bg-orange-500/15 px-2.5 py-1.5">
                          <p className="text-[8px] font-black uppercase tracking-[0.16em] text-orange-200/70">Base</p>
                          <p className="text-[11px] font-black leading-tight text-orange-100">
                            {formatRand(price)}
                            {billingMode === 'monthly' ? '/m' : ''}
                          </p>
                        </div>
                        {domainPreference?.needsRegistration && domainPreference.desiredDomain ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDomainModal(plan.id);
                            }}
                            className="snap-start shrink-0 whitespace-nowrap rounded-xl border border-orange-300/35 bg-orange-500/10 px-2.5 py-1.5 text-left transition-colors hover:border-orange-200/80"
                          >
                            <p className="text-[8px] font-black uppercase tracking-[0.16em] text-orange-300/85">Domain</p>
                            <p className="text-[11px] font-black leading-tight text-orange-100">
                              {domainPreference.desiredDomain}
                            </p>
                            <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-orange-200/65">
                              {domainPreference.availabilityStatus === 'available' && domainPreference.purchaseAmount
                                ? `${formatRand(domainPreference.purchaseAmount)} once-off`
                                : domainPreference.availabilityStatus === 'unavailable'
                                  ? 'Try another'
                                  : domainPreference.availabilityStatus === 'premium'
                                    ? 'Premium'
                                    : 'Tap to check'}
                            </p>
                          </button>
                        ) : null}
                        {pricedFeatureAddOnItems
                          .filter((item) => selectedCatalogIds[plan.id].includes(item.id))
                          .map((item) => (
                            <button
                              key={`mobile-chip-${item.id}`}
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleCatalogItem(plan.id, item);
                              }}
                              className="group/chip snap-start shrink-0 whitespace-nowrap rounded-xl border border-orange-300/35 bg-orange-500/10 px-2.5 py-1.5 text-left transition-colors hover:border-orange-200/80"
                              aria-label={`Remove ${item.label} from package`}
                            >
                              <p className="text-[8px] font-black uppercase tracking-[0.16em] text-orange-300/85">
                                Add-on
                              </p>
                              <p className="text-[11px] font-black leading-tight text-orange-100">
                                {item.label}
                              </p>
                              <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-orange-200/65">
                                +{formatRand(item.adjusted_price)}
                                {billingMode === 'monthly' ? '/m' : ''} · tap to remove
                              </p>
                            </button>
                          ))}
                        {pricedFeatureAddOnItems.filter((item) => selectedCatalogIds[plan.id].includes(item.id))
                          .length === 0 && !domainPreference?.needsRegistration ? (
                          <div className="snap-start shrink-0 whitespace-nowrap rounded-xl border border-dashed border-orange-300/25 bg-black/25 px-2.5 py-1.5">
                            <p className="text-[8px] font-black uppercase tracking-[0.16em] text-orange-300/60">
                              Add-ons
                            </p>
                            <p className="text-[10px] font-bold leading-tight text-orange-100/55">
                              Appear as the consult deduces them
                            </p>
                          </div>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          addCurrentPlanToCart(plan.id);
                        }}
                        className="rounded-full bg-gradient-to-r from-orange-500 to-amber-400 px-4 py-2.5 text-sm font-black text-black shadow-[0_4px_18px_rgba(249,115,22,0.35)] transition-colors hover:from-orange-400 hover:to-amber-300"
                      >
                        Add to cart · {formatRand(checkoutTotal)}
                        {billingMode === 'monthly' ? '/m' : ''}
                      </button>
                      <div
                        aria-hidden="true"
                        className={`pointer-events-none mt-3 h-px bg-gradient-to-r from-transparent ${
                          isLightTheme
                            ? 'via-teal-400/80 shadow-[0_0_18px_rgba(20,184,166,0.55)]'
                            : 'via-orange-200/80 shadow-[0_0_18px_rgba(249,115,22,0.65)]'
                        } to-transparent`}
                      />
                    </div>

                    <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-none lg:border-r lg:pr-5">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.22em] text-orange-300">Project onboarding</p>
                          <h3 className="mt-1 text-xl font-black">Tell us what to build.</h3>
                        </div>
                      </div>

                      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                        {activeMessages.map((message, index) => (
                          <div
                            key={`${message.role}-${index}`}
                            className={
                              message.role === 'client'
                                ? 'ml-10 rounded-2xl bg-orange-500 px-4 py-2.5 text-[14px] font-medium normal-case leading-6 tracking-normal text-black'
                                : 'mr-10 rounded-2xl border border-orange-300/15 bg-white/[0.05] px-4 py-2.5 text-[14px] font-medium normal-case leading-6 tracking-normal text-orange-50/90'
                            }
                          >
                            {message.content}
                          </div>
                        ))}
                        {isIntakeComplete && (
                          <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.4, ease: 'easeOut' }}
                            className="mr-10 flex items-center gap-3 rounded-2xl border border-green-300/35 bg-green-500/[0.08] px-4 py-3 shadow-[inset_0_1px_0_rgba(74,222,128,0.18)]"
                          >
                            <motion.span
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.18, type: 'spring', stiffness: 260, damping: 18 }}
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-400 text-black"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                <motion.path
                                  d="M5 12l5 5L20 7"
                                  initial={{ pathLength: 0 }}
                                  animate={{ pathLength: 1 }}
                                  transition={{ delay: 0.32, duration: 0.55, ease: 'easeOut' }}
                                />
                              </svg>
                            </motion.span>
                            <div className="min-w-0">
                              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-green-200">
                                Spec ready
                              </p>
                              <p className="text-[13px] font-medium leading-5 text-orange-100/85">
                                Review the package on the right and add it to your cart.
                              </p>
                            </div>
                          </motion.div>
                        )}
                        {isThinking && (
                          <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mr-10 inline-flex items-center gap-2.5 rounded-2xl border border-orange-300/15 bg-white/[0.06] px-4 py-3 text-sm text-orange-50/70"
                          >
                            <span className="text-xs font-black uppercase tracking-[0.22em] text-orange-300">
                              Thinking
                            </span>
                            <span className="flex gap-1">
                              {[0, 1, 2].map((dot) => (
                                <motion.span
                                  key={dot}
                                  animate={{ opacity: [0.25, 1, 0.25], y: [0, -2, 0] }}
                                  transition={{ repeat: Infinity, duration: 0.9, delay: dot * 0.12 }}
                                  className="h-1.5 w-1.5 rounded-full bg-orange-300"
                                />
                              ))}
                            </span>
                          </motion.div>
                        )}
                      </div>

                      {chatSuggestions.length > 0 ? (
                        <div className="mt-3 border-t border-white/10 pt-3" onClick={(event) => event.stopPropagation()}>
                          <p className="mb-2 text-[9px] font-black uppercase tracking-[0.2em] text-orange-300/75">
                            Suggested adds
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {chatSuggestions.map((item) => (
                              <button
                                key={`suggest-${item.id}`}
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleCatalogItem(plan.id, item);
                                }}
                                className="rounded-full border border-orange-300/35 bg-orange-500/10 px-3 py-1.5 text-left text-[10px] font-black uppercase tracking-[0.12em] text-orange-200 transition-colors hover:border-orange-200/80 hover:bg-orange-500/20"
                              >
                                + {item.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      <div className="mt-4 flex gap-2">
                        <input
                          value={chatInput}
                          onClick={(event) => event.stopPropagation()}
                          onFocus={() => {
                            if (isBotLocked) {
                              window.dispatchEvent(new CustomEvent('ddd:open-auth', { detail: { mode: 'login' } }));
                            }
                          }}
                          onChange={(event) => setChatInput(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') handleDraftSend();
                          }}
                          disabled={isThinking || isBotLocked || isIntakeComplete}
                          placeholder={
                            isBotLocked
                              ? 'Log in to use the live consult...'
                              : isIntakeComplete
                                ? 'Spec complete — tap Reset to start over'
                                : messagesLeft <= 2 && messagesLeft > 0
                                  ? messagesLeft === 1
                                    ? 'Last message in this brief — include anything still missing.'
                                    : `Plain English (${messagesLeft} messages left including this one).`
                                  : 'Answer in plain English...'
                          }
                          className="min-w-0 flex-1 rounded-full border border-white/10 bg-black/35 px-4 py-3 text-sm text-orange-50 outline-none transition-colors placeholder:text-orange-100/35 focus:border-orange-300/70 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDraftSend();
                          }}
                          disabled={isThinking || isIntakeComplete}
                          className="rounded-full border border-orange-300/40 bg-orange-500/15 px-5 py-3 text-sm font-black text-orange-100 transition-colors hover:border-orange-200/80 hover:bg-orange-500/25 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isThinking ? 'Reading...' : isBotLocked ? 'Login' : isIntakeComplete ? 'Done' : 'Send'}
                        </button>
                      </div>
                      {isBotLocked && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            window.dispatchEvent(new CustomEvent('ddd:open-auth', { detail: { mode: 'login' } }));
                          }}
                          className="mt-3 w-full rounded-2xl border border-orange-300/25 bg-orange-500/[0.08] px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] text-orange-200 transition-colors hover:border-orange-200/70 hover:bg-orange-500/[0.14]"
                        >
                          Log in to start the live brief
                        </button>
                      )}
                    </div>

                    <div className="hidden min-h-0 min-w-0 flex-col lg:flex lg:pl-5">
                      <span className="sr-only">
                        Live package — {plan.name}
                      </span>
                      <div className="flex items-start justify-between gap-3 pt-1">
                        <div className="flex min-w-0 flex-1 items-start gap-3">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleResetConsult(plan.id);
                            }}
                            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-orange-300/35 bg-orange-500/10 text-orange-200 transition-colors hover:border-orange-200/80 hover:bg-orange-500/20"
                            aria-label="Clear and reset consult"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M3 7v6h6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-7 3.3L3 13"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <div
                            className="flex min-w-0 flex-1 items-start gap-x-3 gap-y-2 overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch]"
                            style={{ scrollbarWidth: 'thin' }}
                          >
                            {manualQuickPickAddons.map((pick) => (
                              <ManualAddonSwatchButton
                                key={`quick-${pick.id}`}
                                item={pick}
                                planId={plan.id}
                                isSelected={selectedCatalogIds[plan.id].includes(pick.id)}
                                onToggle={toggleCatalogItem}
                                isLightTheme={isLightTheme}
                              />
                            ))}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setActivePlanId(null);
                            setChatInput('');
                          }}
                          className="mt-0.5 shrink-0 self-start rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-wider text-orange-100 transition-colors hover:border-orange-300/60"
                        >
                          Close
                        </button>
                      </div>

                      <div className="mt-2 min-w-0 space-y-1.5">
                        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-300">
                          Feature add-ons
                        </p>
                        <FeatureAddonsCarousel
                          items={pricedFeatureAddOnItems}
                          planId={plan.id}
                          decision={decision}
                          selectedIds={selectedCatalogIds[plan.id]}
                          domainPreference={domainPreference}
                          billingMode={billingMode}
                          onToggleItem={toggleCatalogItem}
                          isLightTheme={isLightTheme}
                        />
                        {decision.questions.length > 0 ? (
                          <div className="rounded-xl border border-orange-300/15 bg-orange-500/[0.06] px-3 py-2.5">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-300/80">Live read</p>
                            <p className="mt-1 text-xs leading-5 text-orange-100/68">{decision.questions[0]}</p>
                          </div>
                        ) : null}
                      </div>

                      {domainPreference ? (
                        <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-orange-300/20 bg-orange-500/[0.06] px-3.5 py-2.5">
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-300/80">
                              Domain
                            </p>
                            <p className="mt-0.5 truncate text-sm font-black text-orange-100">
                              {domainPreference.needsRegistration
                                ? domainPreference.desiredDomain || 'Pending'
                                : 'Customer-owned'}
                            </p>
                          </div>
                          {domainPreference.needsRegistration ? (
                            <>
                              <span
                                className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${
                                  domainPreference.availabilityStatus === 'available'
                                    ? 'border border-green-300/40 bg-green-500/15 text-green-200'
                                    : domainPreference.availabilityStatus === 'unavailable'
                                      ? 'border border-red-300/40 bg-red-500/15 text-red-200'
                                      : domainPreference.availabilityStatus === 'premium'
                                        ? 'border border-amber-300/40 bg-amber-500/15 text-amber-200'
                                        : 'border border-orange-300/30 bg-orange-500/10 text-orange-200'
                                }`}
                              >
                                {domainPreference.availabilityStatus === 'available' &&
                                domainPreference.purchaseAmount
                                  ? formatRand(domainPreference.purchaseAmount)
                                  : domainPreference.availabilityStatus === 'available'
                                    ? 'Available'
                                    : domainPreference.availabilityStatus === 'unavailable'
                                      ? 'Taken'
                                      : domainPreference.availabilityStatus === 'premium'
                                        ? 'Premium'
                                        : 'Check'}
                              </span>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openDomainModal(plan.id);
                                }}
                                className="shrink-0 rounded-full border border-orange-300/35 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-orange-200 transition-colors hover:bg-orange-400 hover:text-black"
                              >
                                Edit
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
                        <div className="grid gap-4 xl:grid-cols-2">
                          <div>
                            <p className="mb-3 text-[11px] font-black uppercase tracking-[0.22em] text-orange-300">
                              Base package
                            </p>
                            <ul className="m-0 list-none space-y-1.5 p-0">
                              {baseItems.map((item, index) => (
                                <motion.li
                                  key={item.id}
                                  initial={{ opacity: 0, x: -6 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.04, duration: 0.22, ease: 'easeOut' }}
                                >
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleCatalogItem(plan.id, item);
                                    }}
                                    className={`group flex w-full items-start gap-3 rounded-md py-1.5 pl-0.5 pr-2 text-left transition-colors ${
                                      isLightTheme ? 'hover:bg-teal-950/[0.06]' : 'hover:bg-white/[0.04]'
                                    }`}
                                  >
                                    <span
                                      className="mt-2 h-1 w-1 shrink-0 rounded-full"
                                      style={{
                                        backgroundColor: isLightTheme ? '#14b8a6' : '#fb923c',
                                        boxShadow: isLightTheme
                                          ? '0 0 10px rgba(20,184,166,0.35)'
                                          : '0 0 10px rgba(249,115,22,0.35)',
                                      }}
                                      aria-hidden
                                    />
                                    <span className="text-[13px] font-semibold leading-snug text-orange-100/88 transition-colors group-hover:text-orange-50">
                                      {item.label}
                                    </span>
                                  </button>
                                </motion.li>
                              ))}
                            </ul>
                            {sharedBackendSavingEl ? (
                              <div className="mt-4 min-w-0 xl:hidden">{sharedBackendSavingEl}</div>
                            ) : null}
                          </div>

                          <div className="hidden min-h-0 min-w-0 flex-col justify-start xl:flex">
                            {sharedBackendSavingEl ? (
                              sharedBackendSavingEl
                            ) : (
                              <div className="min-h-[120px] shrink-0" aria-hidden />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        {domainRegistrationTotal > 0 ? (
                          <div className="mb-3 rounded-2xl border border-green-300/25 bg-green-500/[0.08] p-3 text-xs text-green-100">
                            <span className="font-black uppercase tracking-[0.14em] text-green-300">Domain due today:</span>{' '}
                            {formatRand(domainRegistrationTotal)} for {domainPreference?.desiredDomain}. Registration only proceeds after payment clears.
                          </div>
                        ) : null}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            addCurrentPlanToCart(plan.id);
                          }}
                          className="w-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 px-5 py-3 font-black text-black transition-colors hover:from-orange-400 hover:to-amber-300"
                        >
                          Add to cart - {formatRand(checkoutTotal)}
                          {billingMode === 'monthly' ? '/m' : ''}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.article>
            );
          })}
        </div>
      </section>

      <section className="relative z-10 mx-auto mt-20 grid max-w-7xl gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[2rem] border border-white/10 bg-white/[0.055] p-7 backdrop-blur-xl">
          <p className="text-sm font-bold uppercase tracking-[0.24em] text-orange-300">Guided intake</p>
          <h2 className="mt-4 text-3xl font-black md:text-5xl">The brief becomes the build system.</h2>
          <p className="mt-5 text-orange-100/70">
            After checkout, the client answers guided questions in a premium flow. We turn that into a polished
            checklist, save it to the portal, and use it as the source of truth for the project.
          </p>
          <div className="mt-7 grid grid-cols-2 gap-3">
            {modules.map((module) => (
              <div key={module} className="rounded-2xl border border-orange-300/20 bg-black/25 p-4 text-sm font-bold text-orange-50/85">
                {module}
              </div>
            ))}
          </div>
        </div>
        <div className="grid gap-4">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: index * 0.08, duration: 0.45 }}
              className="rounded-[1.5rem] border border-white/10 bg-black/35 p-5 backdrop-blur-xl"
            >
              <div className="flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-400 font-black text-black">
                  {index + 1}
                </span>
                <div>
                  <h3 className="text-xl font-black">{step.title}</h3>
                  <p className="mt-2 text-orange-100/68">{step.copy}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {lastBriefMessagePlanId ? (
        <LastBriefMessageModal
          isLightTheme={isLightTheme}
          onConfirm={confirmLastBriefMessageSend}
          onCancel={cancelLastBriefMessageSend}
        />
      ) : null}
      {domainModalPlanId && (
        <DomainCheckModal
          planId={domainModalPlanId}
          input={domainInput}
          onInputChange={setDomainInput}
          isChecking={isDomainChecking}
          checkResult={domainCheckResult}
          errorMessage={domainModalError}
          isLightTheme={isLightTheme}
          alternatives={buildDomainAlternatives(domainInput)}
          onCheck={() => runDomainCheck(domainModalPlanId, domainInput)}
          onTryDifferent={(suggested) => tryDifferentDomain(suggested)}
          onAddToCart={() => addDomainToCart(domainModalPlanId)}
          onSkip={() => skipDomainStep(domainModalPlanId)}
          onClose={closeDomainModal}
        />
      )}
    </main>
  );
};

type LastBriefMessageModalProps = {
  isLightTheme: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const LastBriefMessageModal: React.FC<LastBriefMessageModalProps> = ({ isLightTheme, onConfirm, onCancel }) => {
  const accent = isLightTheme ? '#0f766e' : '#f97316';
  const heading = isLightTheme ? '#06131a' : '#fff7ed';
  const body = isLightTheme ? 'rgba(15,23,42,0.85)' : 'rgba(254,215,170,0.82)';

  return (
    <div
      className="fixed inset-0 z-[199] flex items-center justify-center px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="last-brief-message-title"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-black/65 backdrop-blur-[3px]" aria-hidden />
      <div
        className="relative z-10 w-full max-w-md rounded-2xl border p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)]"
        style={{
          borderColor: isLightTheme ? 'rgba(13,148,136,0.35)' : 'rgba(251,146,60,0.35)',
          background: isLightTheme ? '#f8fafc' : '#070504',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: accent }}>
          Final message
        </p>
        <h4 id="last-brief-message-title" className="mt-2 text-lg font-black leading-snug" style={{ color: heading }}>
          This is your last message in the live brief
        </h4>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: body }}>
          Include anything still missing for the build. After you send, the spec is ready for review unless you tap Reset
          on the package.
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={`rounded-full border px-4 py-2.5 text-xs font-black uppercase tracking-[0.14em] transition-colors ${
              isLightTheme
                ? 'border-slate-300/60 bg-white text-slate-800 hover:bg-slate-50'
                : 'border-white/20 bg-white/5 text-orange-100 hover:border-white/35'
            }`}
          >
            Go back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-full bg-orange-400 px-4 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-black transition-colors hover:bg-orange-300"
          >
            Send it
          </button>
        </div>
      </div>
    </div>
  );
};

type DomainCheckModalProps = {
  planId: ProductTierId;
  input: string;
  onInputChange: (value: string) => void;
  isChecking: boolean;
  checkResult: DomainCheckResult | null;
  errorMessage: string;
  isLightTheme: boolean;
  alternatives: string[];
  onCheck: () => void;
  onTryDifferent: (suggested?: string) => void;
  onAddToCart: () => void;
  onSkip: () => void;
  onClose: () => void;
};

const DomainCheckModal: React.FC<DomainCheckModalProps> = ({
  input,
  onInputChange,
  isChecking,
  checkResult,
  errorMessage,
  isLightTheme,
  alternatives,
  onCheck,
  onTryDifferent,
  onAddToCart,
  onSkip,
  onClose,
}) => {
  const accent = isLightTheme ? '#0f766e' : '#f97316';
  const accentSoft = isLightTheme ? 'rgba(20,184,166,0.16)' : 'rgba(249,115,22,0.16)';
  const accentBorder = isLightTheme ? 'rgba(13,148,136,0.4)' : 'rgba(251,146,60,0.4)';
  const surfaceBg = isLightTheme ? '#f8fafc' : '#070504';
  const textPrimary = isLightTheme ? '#06131a' : '#fff7ed';
  const textMuted = isLightTheme ? 'rgba(8,47,73,0.62)' : 'rgba(254,215,170,0.66)';
  const fieldBg = isLightTheme ? '#ffffff' : 'rgba(0,0,0,0.45)';
  const fieldBorder = isLightTheme ? 'rgba(15,118,110,0.28)' : 'rgba(251,146,60,0.28)';

  const status = checkResult?.availabilityStatus;
  const showResult = Boolean(checkResult && status && status !== 'unchecked');

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isChecking) onCheck();
  };

  const renderResult = () => {
    if (!showResult || !checkResult) return null;
    const checkedDomain = checkResult.domain || input.trim();

    if (status === 'available') {
      return (
        <div
          className="mt-5 rounded-2xl border p-4"
          style={{
            background: 'rgba(34,197,94,0.10)',
            borderColor: 'rgba(34,197,94,0.45)',
          }}
        >
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-xs font-black text-white">✓</span>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-300">Available</p>
          </div>
          <p className="mt-3 text-base font-black" style={{ color: textPrimary }}>{checkedDomain}</p>
          <p className="mt-1 text-sm leading-5" style={{ color: textMuted }}>
            Add it to your cart for <span className="font-black" style={{ color: textPrimary }}>{formatRand(checkResult.purchaseAmount || 150)}</span> once-off. Registration only fires after payment clears.
          </p>
        </div>
      );
    }

    if (status === 'unavailable') {
      return (
        <div
          className="mt-5 rounded-2xl border p-4"
          style={{
            background: 'rgba(239,68,68,0.10)',
            borderColor: 'rgba(239,68,68,0.40)',
          }}
        >
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-500 text-xs font-black text-white">×</span>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-rose-200">Already taken</p>
          </div>
          <p className="mt-3 text-base font-black" style={{ color: textPrimary }}>{checkedDomain}</p>
          <p className="mt-1 text-sm leading-5" style={{ color: textMuted }}>
            Someone owns this one already. Try a different name or another extension below.
          </p>
          {alternatives.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {alternatives.map((alt) => (
                <button
                  key={alt}
                  type="button"
                  onClick={() => onTryDifferent(alt)}
                  className="rounded-full border px-3 py-1.5 text-xs font-black transition-colors"
                  style={{
                    background: accentSoft,
                    borderColor: accentBorder,
                    color: accent,
                  }}
                >
                  Try {alt}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      );
    }

    if (status === 'premium') {
      return (
        <div
          className="mt-5 rounded-2xl border p-4"
          style={{
            background: 'rgba(245,158,11,0.10)',
            borderColor: 'rgba(245,158,11,0.40)',
          }}
        >
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-xs font-black text-black">★</span>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-amber-200">Premium domain</p>
          </div>
          <p className="mt-3 text-base font-black" style={{ color: textPrimary }}>{checkedDomain}</p>
          <p className="mt-1 text-sm leading-5" style={{ color: textMuted }}>
            This is flagged as a premium name. Luke will quote it manually before checkout — it won't be billed automatically.
          </p>
        </div>
      );
    }

    if (status === 'invalid') {
      return (
        <div
          className="mt-5 rounded-2xl border p-4"
          style={{
            background: 'rgba(239,68,68,0.08)',
            borderColor: 'rgba(239,68,68,0.35)',
          }}
        >
          <p className="text-sm font-black uppercase tracking-[0.18em] text-rose-200">Invalid</p>
          <p className="mt-2 text-sm leading-5" style={{ color: textMuted }}>
            {checkResult.message || 'That domain looks malformed. Try something like example.co.za.'}
          </p>
        </div>
      );
    }

    return (
      <div
        className="mt-5 rounded-2xl border p-4"
        style={{
          background: 'rgba(148,163,184,0.10)',
          borderColor: 'rgba(148,163,184,0.40)',
        }}
      >
        <p className="text-sm font-black uppercase tracking-[0.18em]" style={{ color: textPrimary }}>
          Live check unavailable
        </p>
        <p className="mt-2 text-sm leading-5" style={{ color: textMuted }}>
          {checkResult.message || "We couldn't reach the registrar. You can still add this domain and Luke will check it manually before billing the registration."}
        </p>
      </div>
    );
  };

  const showAddToCart = status === 'available' || status === 'check_failed' || status === 'premium' || status === 'manual';
  const addToCartLabel =
    status === 'available'
      ? 'Add this domain to my cart'
      : status === 'premium'
        ? 'Add for manual quote'
        : 'Add anyway (manual check)';

  return (
    <div
      className="fixed inset-0 z-[260] flex items-center justify-center bg-black/70 p-4"
      onMouseDown={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 12, filter: 'blur(10px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="relative w-full max-w-lg overflow-hidden rounded-[2rem] border p-6 shadow-[0_0_70px_rgba(0,0,0,0.45)]"
        style={{
          background: surfaceBg,
          borderColor: accentBorder,
          color: textPrimary,
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div
          className="pointer-events-none absolute inset-x-8 top-0 h-px"
          style={{
            background: `linear-gradient(to right, transparent, ${accent}, transparent)`,
          }}
        />
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl"
          style={{ background: accentSoft }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full border text-base font-black transition-colors"
          style={{
            background: fieldBg,
            borderColor: fieldBorder,
            color: textPrimary,
          }}
        >
          ×
        </button>
        <div className="relative z-10">
          <p
            className="text-[10px] font-black uppercase tracking-[0.24em]"
            style={{ color: accent }}
          >
            Domain check · Live
          </p>
          <h3 className="mt-3 text-3xl font-black leading-tight">
            Find your web address
          </h3>
          <p className="mt-3 text-sm leading-6" style={{ color: textMuted }}>
            Type the address you want and tap <span className="font-black" style={{ color: textPrimary }}>Check</span>. We'll see if it's available right now and only register it after payment clears.
          </p>

          <form onSubmit={handleSubmit}>
            <label
              htmlFor="desired-domain"
              className="mt-5 block text-xs font-black uppercase tracking-[0.16em]"
              style={{ color: textMuted }}
            >
              Desired web address
            </label>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <input
                id="desired-domain"
                value={input}
                onChange={(event) => onInputChange(event.target.value)}
                placeholder="example.co.za"
                className="flex-1 rounded-full border px-4 py-3 text-sm font-bold outline-none transition-colors"
                style={{
                  background: fieldBg,
                  borderColor: fieldBorder,
                  color: textPrimary,
                }}
                autoFocus
              />
              <button
                type="submit"
                disabled={isChecking || !input.trim()}
                className="rounded-full px-6 py-3 text-sm font-black transition-transform hover:scale-[1.02] disabled:cursor-wait disabled:opacity-60"
                style={{
                  background: accent,
                  color: isLightTheme ? '#ffffff' : '#0a0a0a',
                }}
              >
                {isChecking ? 'Checking…' : 'Check availability'}
              </button>
            </div>
          </form>

          {errorMessage ? (
            <p
              className="mt-3 rounded-2xl border p-3 text-sm leading-5"
              style={{
                background: 'rgba(239,68,68,0.08)',
                borderColor: 'rgba(239,68,68,0.35)',
                color: '#fecaca',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          {renderResult()}

          {showResult ? (
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              {showAddToCart ? (
                <button
                  type="button"
                  onClick={onAddToCart}
                  className="flex-1 rounded-full px-5 py-3 text-sm font-black transition-transform hover:scale-[1.02]"
                  style={{
                    background: status === 'available' ? '#10b981' : accent,
                    color: '#0a0a0a',
                  }}
                >
                  {addToCartLabel}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onTryDifferent()}
                className="rounded-full border px-5 py-3 text-sm font-black transition-colors"
                style={{
                  background: 'transparent',
                  borderColor: fieldBorder,
                  color: textPrimary,
                }}
              >
                Try a different domain
              </button>
            </div>
          ) : null}

          <div
            className="mt-6 flex items-center justify-between border-t pt-4 text-xs font-bold"
            style={{ borderColor: fieldBorder, color: textMuted }}
          >
            <span>I'd rather sort the domain myself.</span>
            <button
              type="button"
              onClick={onSkip}
              className="font-black uppercase tracking-[0.16em] underline-offset-4 hover:underline"
              style={{ color: accent }}
            >
              Skip this step
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductPage;
